/**
 * Google Drive Picker integration.
 *
 * Lazy-loads the Google Identity Services and Picker scripts on demand,
 * authenticates the user with a read-only Drive scope, lets them pick a
 * PDF/CSV/TXT file, and returns the bytes as a `File` for the rest of
 * the pipeline to consume (parse → dedup → save).
 *
 * No secrets are stored in the repo: the OAuth Client ID is read from
 * localStorage at runtime, set in the Settings screen.
 */

const GIS_SRC    = 'https://accounts.google.com/gsi/client';
const PICKER_SRC = 'https://apis.google.com/js/api.js';
const SCOPE      = 'https://www.googleapis.com/auth/drive.readonly';

const LS_KEY = 'btv2.googleClientId';

declare global {
  interface Window {
    google?: {
      accounts: { oauth2: { initTokenClient: (c: any) => any; revoke: (t: string, d?: () => void) => void } };
      picker?: any;
    };
    gapi?: { load: (lib: string, opts: { callback: () => void; onerror?: () => void }) => void };
  }
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) return resolve();
    const el = document.createElement('script');
    el.id = id;
    el.src = src;
    el.async = true;
    el.defer = true;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(el);
  });
}

async function ensureGoogleApis(): Promise<void> {
  if (!window.google?.picker) {
    await loadScript(PICKER_SRC, 'gapi-script');
    await new Promise<void>((resolve, reject) => {
      const tryReady = () => {
        if (window.gapi?.load) resolve();
        else setTimeout(tryReady, 50);
      };
      tryReady();
      setTimeout(() => reject(new Error('gapi failed to initialise')), 10_000);
    });
    await new Promise<void>((resolve, reject) => {
      window.gapi!.load('picker', { callback: resolve, onerror: () => reject(new Error('picker load failed')) });
    });
  }
  if (!window.google?.accounts?.oauth2) {
    await loadScript(GIS_SRC, 'gis-script');
  }
}

let tokenClient: any = null;
let cachedToken: string | null = null;
let cachedTokenExp = 0;

function getClientId(): string | null {
  try { return localStorage.getItem(LS_KEY); } catch { return null; }
}

function requestToken(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!tokenClient) {
      tokenClient = window.google!.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: SCOPE,
        callback: () => { /* replaced per request */ },
      });
    }
    tokenClient.callback = (resp: any) => {
      if (resp.error) return reject(new Error(resp.error_description || resp.error));
      cachedToken    = resp.access_token;
      cachedTokenExp = Date.now() + (resp.expires_in ?? 3600) * 1000 - 60_000;
      resolve(resp.access_token);
    };
    tokenClient.requestAccessToken({ prompt: cachedToken ? '' : 'consent' });
  });
}

async function ensureToken(forceRefresh = false): Promise<string> {
  const clientId = getClientId();
  if (!clientId) throw new Error('NO_CLIENT_ID');
  if (forceRefresh) cachedToken = null;
  if (cachedToken && Date.now() < cachedTokenExp) return cachedToken;
  return requestToken(clientId);
}

async function fetchDriveFile(fileId: string): Promise<Blob> {
  const doFetch = async (t: string) => fetch(
    `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`,
    { headers: { Authorization: `Bearer ${t}` } },
  );

  let res = await doFetch(await ensureToken());
  if (res.status === 401) {
    const fresh = await ensureToken(true);
    res = await doFetch(fresh);
  }
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error?.message ? ` — ${body.error.message}` : '';
    } catch { /* non-JSON body */ }
    throw new Error(`Drive fetch failed: ${res.status}${detail}`);
  }
  return res.blob();
}

function guessMime(name: string): string {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.csv')) return 'text/csv';
  if (lower.endsWith('.txt')) return 'text/plain';
  return 'application/octet-stream';
}

export interface DrivePickResult {
  file: File;
  fileName: string;
}

export function isDriveConfigured(): boolean {
  return !!getClientId();
}
export function getDriveClientId(): string | null { return getClientId(); }

export function setDriveClientId(id: string | null) {
  try {
    if (id && id.trim()) localStorage.setItem(LS_KEY, id.trim());
    else localStorage.removeItem(LS_KEY);
  } catch { /* private mode */ }
}

export class DriveCancelledError extends Error {
  constructor() { super('Pick cancelled'); this.name = 'DriveCancelledError'; }
}

/** Returns the picked file, null if the user cancelled, or throws on real errors. */
export async function pickFromDrive(): Promise<DrivePickResult | null> {
  await ensureGoogleApis();
  const token = await ensureToken();

  return new Promise<DrivePickResult | null>((resolve, reject) => {
    const view = new window.google!.picker.DocsView()
      .setMimeTypes('application/pdf,text/csv,text/plain,application/csv,application/vnd.ms-excel')
      .setMode(window.google!.picker.DocsViewMode.LIST);
    const picker = new window.google!.picker.PickerBuilder()
      // No setAppId — we authenticate with an OAuth Client ID (web app),
      // so the Picker uses that flow rather than the older API-key + App
      // ID flow. Passing a non-numeric App ID here used to silently drop
      // the OAuth token and the file fetch would then 401/403.
      .setOAuthToken(token)
      .addView(view)
      .setCallback(async (data: any) => {
        if (window.console && console.debug) console.debug('[gdrive] picker callback', data);
        if (data.action !== window.google!.picker.Action.PICKED) {
          resolve(null); // user cancelled or backed out
          return;
        }
        const doc = data.docs?.[0];
        if (!doc?.id) {
          reject(new Error('Picker returned no document (the file may be a Drive shortcut or empty selection).'));
          return;
        }
        try {
          const blob = await fetchDriveFile(doc.id);
          if (blob.size === 0) {
            reject(new Error('Drive returned an empty file. The file may be a shortcut, a Google Workspace doc, or protected.'));
            return;
          }
          const fileName = doc.name || 'statement.pdf';
          const file = new File([blob], fileName, { type: blob.type || guessMime(fileName) });
          resolve({ file, fileName });
        } catch (err) {
          reject(err);
        }
      })
      .build();
    picker.setVisible(true);
  });
}
